import React, { useEffect, useMemo, useRef, useState, lazy, Suspense, useCallback } from 'react'
import { updateProjectsStagesStatus, IProjectStage, ISortProject, handleUpdateCurrentActiveStepApi, Stagestep, findStepIndex, CurrentStageStep, findPrevStep, findNextStep } from '../../../modules/project'
import { Field, Form, Formik, FormikProps, getIn, setIn } from 'formik'
import { isEqual } from 'lodash'
import { KTIcon } from '../../../../_theme/helpers'
import NotFound from '../../apps/components/NotFound'
import { useSnackbar } from '../../../../_theme/context/SnackbarContext'
import { ApiResponse, IDocumentData } from '../../core/_models'
import { useNavigate } from 'react-router-dom'
import { getProjectStageLink } from '../../../../utils/config'
import SubmitConfirmationModal from '../../apps/components/SubmitConfirmationModal'
import { useLeftStepper } from '../../../../_theme/context/LeftStepperContext'
import StepChangeAlertModal from '../../apps/components/StepChangeAlertModal'
import { BarChart, Briefcase, CircleAlert, CircleDot, FileText, Layers, Send, Shield, Target, TrendingUp, Users, Zap } from 'lucide-react'
import StepIconSidebar from './StepIconSidebar'
import DynamicSymbol from '../../apps/components/DynamicSymbol'
import ThemeTooltip from '../../apps/components/UI/ThemeTooltip'
import { IInfrastructurePlanningStageSchema, infrastructurePlanningStageSchema, stepTabSchemas, findIncompleteStepTab } from '../components/IP-steps/core/_validation'
import { IInfrastructurePlanningTypes, IProjectScope, IInvestmentNeedsPolicyIncentives, IStrategicFitSummaryDashboard, ISocioEconomicContext, ISWOTAnalysis, IKeyBusinessDrivers, ITechnicalOptionsAnalysis, IStrategicFuturesAndResilienceTesting, ISupplementaryInfrastructurePlanning, STEP_TABS } from '../components/IP-steps/core/_models'
import { saveProjectStageOne } from '../components/IP-steps/core/_request'
import { infrastructurePlanningDefaultValues } from '../components/IP-steps/core/defaultValues'

//  Lazy load all step components
const SubmitStep = lazy(() => import('../components/common/SubmitStep').then(module => ({ default: module.SubmitStep })));
import { getApiErrorMessage } from "../../../../utils/utils";
const ProjectScope = lazy(() => import('../components/IP-steps/ProjectScope').then(module => ({ default: module.ProjectScope })));
const Justification = lazy(() => import('../components/IP-steps/Justification').then(module => ({ default: module.Justification })));
const Objectives = lazy(() => import('../components/IP-steps/Objectives').then(module => ({ default: module.Objectives })));
const StrategicSignificanceImpactFramework = lazy(() => import('../components/IP-steps/StrategicSignificanceImpactFramework').then(module => ({ default: module.StrategicSignificanceImpactFramework })));
const SupplementaryInfrastructurePlanning = lazy(() => import('../components/IP-steps/SupplementaryInfrastructurePlanning').then(module => ({ default: module.SupplementaryInfrastructurePlanning })));
const MarketSizeAndDemandAnalysis = lazy(() => import('../components/IP-steps/MarketSizeAndDemandAnalysis').then(module => ({ default: module.MarketSizeAndDemandAnalysis })));
const SectorSpecificAnalysis = lazy(() => import('../components/IP-steps/SectorSpecificAnalysis').then(module => ({ default: module.SectorSpecificAnalysis })));
const BenefitsAndDesiredOutcomes = lazy(() => import('../components/IP-steps/BenefitsAndDesiredOutcomes').then(module => ({ default: module.BenefitsAndDesiredOutcomes })));
const InvestmentNeedsPolicyIncentives = lazy(() => import('../components/IP-steps/InvestmentNeedsPolicyIncentives').then(module => ({ default: module.InvestmentNeedsPolicyIncentives })));
const StrategicFitSummaryDashboard = lazy(() => import('../components/IP-steps/StrategicFitSummaryDashboard').then(module => ({ default: module.StrategicFitSummaryDashboard })));
const SocioEconomicContext = lazy(() => import('../components/IP-steps/SocioEconomicContext').then(module => ({ default: module.SocioEconomicContext })));
const SWOTAnalysis = lazy(() => import('../components/IP-steps/SWOTAnalysis').then(module => ({ default: module.SWOTAnalysis })));
const KeyBusinessDrivers = lazy(() => import('../components/IP-steps/KeyBusinessDrivers').then(module => ({ default: module.KeyBusinessDrivers })));
const TechnicalOptionsAnalysis = lazy(() => import('../components/IP-steps/TechnicalOptionsAnalysis').then(module => ({ default: module.TechnicalOptionsAnalysis })));
const DocumentStep = lazy(() => import('../components/common/DocumentStep').then(module => ({ default: module.DocumentStep })));
const SG1ValidationRule = lazy(() => import('../components/IP-steps/SG1ValidationRule').then(module => ({ default: module.SG1ValidationRule })));
const StrategicFuturesAndResilienceTesting = lazy(() => import('../components/IP-steps/StrategicFuturesAndResilienceTesting').then(module => ({ default: module.StrategicFuturesAndResilienceTesting })));
//  Import API functions
import {
    updateProjectScopeData,
    updateSupplementaryInfrastructurePlanning,
    saveStepTab,
    updateInvestmentNeedsPolicyIncentives,
    updateStrategicFitSummaryDashboard,
    updateSocioEconomicContext,
    updateSWOTAnalysis,
    updateKeyBusinessDrivers,
    updateTechnicalPreferredOption,
    uploadStrategicFuturesDocuments,
} from '../components/IP-steps/core/_request'
import { updateProjectDocuments } from '../core/document/_request'
import { toFormFields } from '../core/formFields'
import { LogError } from '../../../../utils/GlobalLogging'

type FormAction = 'save' | 'save-next';

type ExtendedInfrastructurePlanningTypes = IInfrastructurePlanningTypes & {
    formAction?: FormAction;
}

type StepComponentProps = {
    submitLoader: boolean;
    project_id: string;
    disableFormFields: boolean;
    setInitValues: React.Dispatch<React.SetStateAction<IInfrastructurePlanningTypes>>;
    isApprover: boolean;
    stage: IProjectStage;
    handleProjectRequestSubmit: (stageStatus: string, remarks: string) => void;
    setIsAllowNextStep: React.Dispatch<React.SetStateAction<boolean>>
    stepTab: number;
    setStepTab: (tabId: number) => void;
}

const FIRST_STEP_TAB = 1;

type Props = {
    project: ISortProject;
    stage: IProjectStage;
    isApprover: boolean;
    user_id: string;
    readonlyAccess: boolean;
    canSubmit: boolean;
}

type StageSubmitData = {
    user_id: string;
    project_id: string;
    dk_stage: string;
}

type StageStatusUpdateData = StageSubmitData & {
    project_stage_id: string;
    action: string;
    remarks: string;
}

// Stage statuses in which the stage can no longer be edited.
const READ_ONLY_STATUSES = ["stage_status_3", "stage_status_4", "stage_status_6"];

const baseStepperData: Stagestep[] = [
    { title: "Project Scope", key: "project_scope", icon: <FileText size={22} />, iconColor: "text-primary", iconBackground: "bg-light-primary", components: ProjectScope, showIndex: false, },
    { title: "Justification", key: "justification", icon: <CircleAlert size={22} />, iconColor: "text-danger", iconBackground: "bg-light-danger", components: Justification, showIndex: false },
    { title: "Objectives (Draft SMART Framework)", key: "objectives", icon: <Target size={22} />, iconColor: "text-success", iconBackground: "bg-light-success", components: Objectives, showIndex: false },
    { title: "Supplementary Infrastructure & Cross-Functional Planning", key: "supplementary_infrastructure_planning", icon: <Layers size={22} />, iconColor: "text-primary", iconBackground: "bg-light-primary", components: SupplementaryInfrastructurePlanning, showIndex: false },
    { title: "Strategic Significance & Impact Framework", key: "strategic_significance_impact_framework", icon: <Shield size={22} />, iconColor: "text-primary", iconBackground: "bg-light-primary", components: StrategicSignificanceImpactFramework, showIndex: false },
    { title: "Strategic Fit Summary", key: "strategic_fit_summary_dashboard", icon: <CircleDot size={22} />, iconColor: "text-success", iconBackground: "bg-light-success", components: StrategicFitSummaryDashboard, showIndex: false },
    { title: "Sector Specific Analysis", key: "sector_specific_analysis", icon: <BarChart size={22} />, iconColor: "text-info", iconBackground: "bg-light-info", components: SectorSpecificAnalysis, showIndex: false },
    { title: "Market Size And Demand Analysis", key: "market_size_and_demand_analysis", icon: <TrendingUp size={22} />, iconColor: "text-primary", iconBackground: "bg-light-primary", components: MarketSizeAndDemandAnalysis, showIndex: false },
    { title: "Benefits And Desired Outcomes", key: "benefits_and_desired_outcomes", icon: <Target size={22} />, iconColor: "text-success", iconBackground: "bg-light-success", components: BenefitsAndDesiredOutcomes, showIndex: false },
    { title: "Socio-Economic Context (Pre-CBA Input Stage)", key: "socio_economic_context", icon: <Users size={22} />, iconColor: "text-warning", iconBackground: "bg-light-warning", components: SocioEconomicContext, showIndex: false },
    { title: "SWOT Analysis (Strategic Positioning)", key: "swot_analysis", icon: <Shield size={22} />, iconColor: "text-info", iconBackground: "bg-light-info", components: SWOTAnalysis, showIndex: false },
    { title: "Key Business Drivers (WHY)", key: "key_business_drivers", icon: <Briefcase size={22} />, iconColor: "text-info", iconBackground: "bg-light-info", components: KeyBusinessDrivers, description: "Draft SMART Framework", showIndex: false },
    { title: "Technical Options Analysis (Mandatory)", key: "technical_options_analysis", icon: <Zap size={22} />, iconColor: "text-warning", iconBackground: "bg-light-warning", components: TechnicalOptionsAnalysis, showIndex: false },
    { title: "Investment Needs & Policy Incentives", key: "investment_needs_policy_incentives", icon: <TrendingUp size={22} />, iconColor: "text-success", iconBackground: "bg-light-success", components: InvestmentNeedsPolicyIncentives, showIndex: false },
    { title: "Strategic Futures and Resilience Testing", key: "strategic_futures_and_resilience_testing", icon: <TrendingUp size={22} />, iconColor: "text-info", iconBackground: "bg-light-info", components: StrategicFuturesAndResilienceTesting, showIndex: false },
    { title: "Documents", key: "documents", icon: <FileText size={22} />, iconColor: "text-primary", iconBackground: "bg-light-primary", components: DocumentStep, showIndex: false },
    { title: "SG1 Validation Rules (Built-in NT compliance Logic)", description: "Before submitting SG1, ensure your business case meets these mandatory requirements:", key: "sg1_validation_rule", icon: <CircleAlert size={22} />, iconColor: "text-danger", iconBackground: "bg-light-danger", components: SG1ValidationRule, showIndex: false, showContinue: true },
];

const InfrastructurePlanning: React.FC<Props> = ({ user_id, project, stage, isApprover, readonlyAccess, canSubmit }) => {

    const stepperData: Stagestep[] = useMemo(() => {
        return [
            ...baseStepperData,
            {
                title: stage.send_to_approval === "yes" ? "Approval" : "Submit",
                key: "approval_submit",
                showIndex: false,
                icon: <Send size={22} />,
                iconColor: "text-primary",
                iconBackground: "bg-primary-light",
                description: stage.send_to_approval === "yes" ? "Submit the project for approval" : "Submit the project",
                components: SubmitStep
            }
        ];
    }, [stage.send_to_approval]);

    const { leftStepperHide } = useLeftStepper();
    const project_id = project.project_id;
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [submitLoader, setSubmitLoader] = useState<boolean>(false);
    const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = useState(false);
    const [stepChangeAlertModal, setStepChnageAlertModal] = useState(false);
    const [pendingStep, setPendingStep] = useState<string | null>(null);
    const [validateOnStepChange, setValidateOnStepChange] = useState(false);
    const [isAllowNextStep, setIsAllowNextStep] = useState(true);
    const [currentStep, setCurrentStep] = useState<CurrentStageStep>(() => {
        const stored = stage.active_step?.toString() || null;
        const initialStep = findStepIndex(stepperData, stored || "", true)
        if (stored && initialStep.step.key !== stored) {
            console.warn(`Invalid step key: ${stored}`);
        }
        return initialStep;
    });

    const formikRef = useRef<FormikProps<ExtendedInfrastructurePlanningTypes>>(null);
    // Opens the tab the user asked for while the current one had unsaved edits.
    const [pendingTabChange, setPendingTabChange] = useState<{ open: () => void } | null>(null);

    // The open tab of a tab-wise step (a STEP_TABS tab id), kept with the step it was opened on.
    // A newly opened step reads its first tab in the same render, so a tab left over from the
    // previous step never reaches it.
    const [openTab, setOpenTab] = useState<{ step: CurrentStageStep; tabId: number }>(
        () => ({ step: currentStep, tabId: FIRST_STEP_TAB })
    );
    const stepTab = openTab.step === currentStep ? openTab.tabId : FIRST_STEP_TAB;
    const setStepTab = useCallback((tabId: number) => {
        setOpenTab({ step: currentStep, tabId });
    }, [currentStep]);

    // The open step's tabs when it saves one tab at a time.
    const stepTabs = STEP_TABS[currentStep.step.key];
    const openTabFields = useMemo<readonly string[]>(
        () => stepTabs?.find((tab) => tab.tabId === stepTab)?.fields ?? [],
        [stepTabs, stepTab]
    );

    const [currentSchema, setCurrentSchema] = useState(() =>
        infrastructurePlanningStageSchema[stepperData[0].key as IInfrastructurePlanningStageSchema]
    );

    const [initValues, setInitValues] = useState<IInfrastructurePlanningTypes>(
        infrastructurePlanningDefaultValues.project_scope as IInfrastructurePlanningTypes
    );

    // Tab-wise steps validate only the open tab; a tab with no required fields has no schema.
    const validationSchema = useMemo(
        () => (stepTabs ? stepTabSchemas[currentStep.step.key]?.[stepTab] : currentSchema),
        [stepTabs, currentStep.step.key, stepTab, currentSchema]
    );

    // Steps that load their values may replace them without formAction; the form always starts with
    // one, so the hidden formAction input stays controlled when a save resets it to its initial value.
    const formInitialValues = useMemo(
        () => ({ ...initValues, formAction: (initValues as ExtendedInfrastructurePlanningTypes)?.formAction ?? "" }) as ExtendedInfrastructurePlanningTypes,
        [initValues]
    );

    const disableFormFields = useMemo(() => {
        return isApprover || readonlyAccess || READ_ONLY_STATUSES.includes(stage.dk_stage_status);
    }, [isApprover, readonlyAccess, stage.dk_stage_status]);

    useEffect(() => {
        setCurrentSchema(infrastructurePlanningStageSchema[currentStep.step.key as IInfrastructurePlanningStageSchema]);
        setInitValues(infrastructurePlanningDefaultValues[currentStep.step.key as keyof typeof infrastructurePlanningDefaultValues] as IInfrastructurePlanningTypes);
        setIsAllowNextStep(currentStep.step?.isAllowNextStep === false ? false : true);
    }, [currentStep]);

    const handleUpdateActiveStep = useCallback(async (activeStep: string): Promise<void> => {
        const response = await handleUpdateCurrentActiveStepApi(stage.dk_stage, activeStep, Number(project_id)) as ApiResponse;
        if (response.status !== "ok") {
            showSnackbar(response.message, 'danger');
        }
    }, [stage.dk_stage, project_id, showSnackbar]);

    // Save from the "data will be lost" prompt: submits the form, so the step is validated
    // first, and opens the other step only once the save succeeded.
    const handleSaveAndContinue = useCallback(async (): Promise<void> => {
        const formik = formikRef.current;
        const target = pendingStep;
        if (!formik || !target) return;

        await formik.setFieldValue("formAction", "save", false);
        // Typed as void, but resolves to submitStep's result, or undefined when validation fails.
        const saved: unknown = await formik.submitForm();
        setStepChnageAlertModal(false);
        setPendingStep(null);
        if (saved === true) {
            setCurrentStep(findStepIndex(stepperData, target));
        }
    }, [pendingStep, stepperData]);

    const confirmStepChange = useCallback((): void => {
        if (pendingStep) {
            setCurrentStep(findStepIndex(stepperData, pendingStep));
            setPendingStep(null);
        }
        setStepChnageAlertModal(false);
    }, [pendingStep, stepperData]);

    // Resolves true when the step was saved.
    const submitStep = useCallback(async (values: ExtendedInfrastructurePlanningTypes): Promise<boolean> => {
        if (disableFormFields) return false;

        setSubmitLoader(true);
        let projectStageResponse: ApiResponse = { status: "ok", message: "Data updated successfully", data: null, errorCode: "" };

        try {
            const stepKey = currentStep.step.key;
            const projectId = Number(project_id);

            const handlers: Record<string, () => Promise<ApiResponse>> = {
                project_scope: async () => updateProjectScopeData(projectId, values as IProjectScope),
                // Only the tabs without a section save through the step handler: root causes save from their popup and supporting documents upload from theirs.
                justification: async () => ({ status: "ok", message: "Justification saved successfully", data: null, errorCode: "" }),
                // Every Objectives tab has no section: its rows save from their popups, so a tab's Save only validates it.
                objectives: async () => ({ status: "ok", message: "Objectives saved successfully", data: null, errorCode: "" }),
                supplementary_infrastructure_planning: async () => updateSupplementaryInfrastructurePlanning(projectId, values as ISupplementaryInfrastructurePlanning),
                // Every benefit category tab has no section: its outcomes are assigned from its popup, so a tab's Save only validates it.
                benefits_and_desired_outcomes: async () => ({ status: "ok", message: "Benefits and desired outcomes saved successfully", data: null, errorCode: "" }),
                socio_economic_context: async () => updateSocioEconomicContext(projectId, values as ISocioEconomicContext),
                swot_analysis: async () => updateSWOTAnalysis(projectId, values as ISWOTAnalysis),
                key_business_drivers: async () => updateKeyBusinessDrivers(projectId, values as IKeyBusinessDrivers),
                technical_options_analysis: async () => updateTechnicalPreferredOption({ ...(values as ITechnicalOptionsAnalysis), project_id: projectId }),
                investment_needs_policy_incentives: async () => updateInvestmentNeedsPolicyIncentives(projectId, values as IInvestmentNeedsPolicyIncentives),
                strategic_fit_summary_dashboard: async () => updateStrategicFitSummaryDashboard(projectId, values as IStrategicFitSummaryDashboard),
                // Only the Supporting Documents tab, which has no section, saves through the step handler.
                strategic_futures_and_resilience_testing: async () => uploadStrategicFuturesDocuments(projectId, values as IStrategicFuturesAndResilienceTesting),
                // Tabs with a section save through saveStepTab; supporting documents upload from their popup.
                sector_specific_analysis: async () => ({ status: "ok", message: "Sector specific analysis updated successfully", data: null, errorCode: "" }),
                market_size_and_demand_analysis: async () => ({ status: "ok", message: "Market size and demand analysis updated successfully", data: null, errorCode: "" }),
                documents: async () => updateProjectDocuments({ documents: (values as IDocumentData).documents, project_id: projectId, stage_id: stage.dk_stage }),
                sg1_validation_rule: async () => ({ status: "ok", message: "Validation passed", data: null, errorCode: "" }),
            };

            // A tab of a tab-wise step saves just that tab's fields to its sub-section.
            const openStepTab = stepTabs?.find((tab) => tab.tabId === stepTab);
            const sectionTab = openStepTab?.section ? { ...openStepTab, section: openStepTab.section } : undefined;
            const handler = sectionTab
                ? () => saveStepTab(projectId, sectionTab, values)
                : handlers[stepKey];

            if (!handler) {
                projectStageResponse = { status: "error", message: "Something went wrong", data: null, errorCode: "" };
            } else {
                projectStageResponse = await handler();
            }

            if (projectStageResponse.status === "ok") {
                showSnackbar(projectStageResponse.message, 'success');
                let activeStep = currentStep.step.key;
                const isSaveNext = values.formAction === "save-next";
                const openNextStep = () => {
                    const nextstep = findNextStep(currentStep, stepperData);
                    activeStep = nextstep.step.key;
                    setCurrentStep(nextstep);
                };

                if (stepTabs) {
                    // Only the saved tab comes back: mark it saved and keep the
                    // other tabs' values, including any unsaved edits.
                    const formik = formikRef.current;
                    let tabValues = values;
                    if (formik) {
                        const savedFields = projectStageResponse.data ? toFormFields(projectStageResponse.data, values) : {};
                        formik.resetForm({
                            values: { ...formik.initialValues, ...savedFields } as ExtendedInfrastructurePlanningTypes,
                        });
                        // formAction is not a saved value; left set, it would keep the form dirty.
                        tabValues = { ...values, ...savedFields, formAction: formik.initialValues.formAction } as ExtendedInfrastructurePlanningTypes;
                        void formik.setValues(tabValues, false);
                    }

                    // Save & Next opens the next tab; on the last tab it leaves the step only once
                    // every tab is complete, otherwise it opens the first incomplete tab with its errors.
                    const nextStepTab = stepTabs[stepTabs.findIndex((tab) => tab.tabId === stepTab) + 1];
                    if (isSaveNext && nextStepTab) {
                        setStepTab(nextStepTab.tabId);
                    } else if (isSaveNext) {
                        const incomplete = await findIncompleteStepTab(currentStep.step.key, tabValues);
                        if (incomplete) {
                            setStepTab(incomplete.tabId);
                            formik?.setErrors(incomplete.errors);
                            void formik?.setTouched(
                                Object.keys(incomplete.errors).reduce((acc, key) => setIn(acc, key, true), {}),
                                false,
                            );
                            showSnackbar(`Complete ${incomplete.title} before continuing.`, "warning");
                        } else {
                            openNextStep();
                        }
                    }
                } else if (isSaveNext) {
                    openNextStep();
                } else {
                    // What was submitted is now saved, updated with what the API sent back. formAction
                    // is not a saved value; left set, it would keep the form dirty.
                    const formik = formikRef.current;
                    if (formik) {
                        const savedFields = projectStageResponse.data ? toFormFields(projectStageResponse.data, values) : {};
                        formik.resetForm({
                            values: { ...values, ...savedFields, formAction: formik.initialValues.formAction } as ExtendedInfrastructurePlanningTypes,
                        });
                    }
                }

                if (stage.active_step !== activeStep) {
                    await handleUpdateActiveStep(activeStep);
                }
                return true;
            } else {
                showSnackbar(projectStageResponse.message || "An error occurred while saving", 'danger');
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, "An error occurred while saving"), 'danger');
            LogError("An error occurred while saving", error, "InfrastructurePlanning");
        } finally {
            setSubmitLoader(false);
        }
        return false;
    }, [disableFormFields, currentStep, project_id, stepTabs, stepTab, setStepTab, stepperData, stage.active_step, stage.dk_stage, handleUpdateActiveStep, showSnackbar]);

    // Switching tabs in a tab-wise step asks first when the open tab has unsaved edits,
    // because Save only covers the open tab.
    const changeStepTab = useCallback((tabId: number) => {
        const formik = formikRef.current;
        if (!formik || tabId === stepTab) return;

        const hasUnsavedEdits = !disableFormFields && openTabFields.some(
            (field) => !isEqual(getIn(formik.values, field), getIn(formik.initialValues, field))
        );
        if (hasUnsavedEdits) {
            setPendingTabChange({ open: () => setStepTab(tabId) });
            return;
        }
        setStepTab(tabId);
    }, [stepTab, setStepTab, openTabFields, disableFormFields]);

    // Continue without saving: put the open tab back to its saved values, then switch.
    const discardAndChangeTab = useCallback(() => {
        const formik = formikRef.current;
        if (formik && pendingTabChange) {
            let restored = formik.values;
            openTabFields.forEach((field) => {
                restored = setIn(restored, field, getIn(formik.initialValues, field));
            });
            void formik.setValues(restored, false);
            formik.setErrors({});
            pendingTabChange.open();
        }
        setPendingTabChange(null);
    }, [openTabFields, pendingTabChange]);

    // Save & Continue: save the open tab (with its validation), and switch only if it saved.
    const saveAndChangeTab = useCallback(async () => {
        const formik = formikRef.current;
        const target = pendingTabChange;
        if (!formik || !target) return;

        await formik.setFieldValue("formAction", "save", false);
        // Typed as void, but resolves to submitStep's result, or undefined when validation fails.
        const saved: unknown = await formik.submitForm();
        setPendingTabChange(null);
        if (saved === true) {
            target.open();
        }
    }, [pendingTabChange]);

    const handleSubmitStage = useCallback((): void => {
        setShowSubmitConfirmationModal(true);
    }, []);

    useEffect(() => {
        if (!validateOnStepChange) return;

        const timeoutId = setTimeout(() => {
            if (formikRef.current) {
                formikRef.current.validateForm().then((errors) => {
                    const touchedFields = Object.keys(errors).reduce<Record<string, boolean>>((acc, key) => {
                        acc[key] = true;
                        return acc;
                    }, {});
                    formikRef.current?.setTouched(touchedFields);
                });
            }
        }, 100);

        setValidateOnStepChange(false);

        return () => clearTimeout(timeoutId);
    }, [initValues, validateOnStepChange]);

    const handleConfirmSubmitStage = useCallback(async (): Promise<void> => {
        setSubmitLoader(true);
        const data: StageSubmitData = { user_id, project_id, dk_stage: stage.dk_stage };

        try {
            const stageSubmitResp = await saveProjectStageOne(data) as ApiResponse;
            if (stageSubmitResp.status === "ok") {
                // if (stage.send_to_approval === "no") {
                //     await getProjectReport(data, stage.dk_stage);
                // }
                showSnackbar(stageSubmitResp.message, 'success');
                navigate(getProjectStageLink(project_id));
            } else {
                showSnackbar(stageSubmitResp.message || 'Failed to submit stage', 'danger');
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, "Failed to submit stage"), 'danger');
            LogError("Failed to submit stage", error, "InfrastructurePlanning");
        } finally {
            setSubmitLoader(false);
            setShowSubmitConfirmationModal(false);
        }
    }, [user_id, project_id, stage.dk_stage, navigate, showSnackbar]);

    const handleContinue = useCallback(() => {
        const nextStep = findNextStep(currentStep, stepperData);
        setCurrentStep(nextStep);
        handleUpdateActiveStep(nextStep.step.key);
    }, [currentStep, stepperData, handleUpdateActiveStep]);

    const prevStep = useCallback((dirty: boolean): void => {
        const prev = findPrevStep(currentStep, stepperData);

        if (!disableFormFields && dirty) {
            setPendingStep(prev.step.key);
            setStepChnageAlertModal(true);
            return;
        }
        setCurrentStep(prev);
    }, [currentStep, stepperData, disableFormFields]);

    const goToStep = useCallback((key: string, dirty: boolean): void => {
        // Re-selecting the open step would reset its form to defaults without refetching its data.
        if (key === currentStep.step.key) {
            return;
        }
        if (!disableFormFields && dirty) {
            setPendingStep(key);
            setStepChnageAlertModal(true);
            return;
        }
        setCurrentStep(findStepIndex(stepperData, key));
    }, [stepperData, disableFormFields, currentStep.step.key]);

    const handleFormSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>, handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void, setFieldValue: FormikProps<ExtendedInfrastructurePlanningTypes>['setFieldValue']): Promise<void> => {
        e.preventDefault();

        // The clicked button says which action this is: 'save' or 'save-next'.
        const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
        const action = submitter?.value;
        if (action) {
            await setFieldValue("formAction", action);
        }
        handleSubmit(e);
    }, []);

    const handleProjectRequestSubmit = useCallback(async (stageStatus: string, remarks: string): Promise<void> => {
        setSubmitLoader(true);
        const data: StageStatusUpdateData = {
            user_id,
            project_id,
            dk_stage: stage.dk_stage,
            project_stage_id: stage.project_stage_id,
            action: stageStatus,
            remarks
        };

        try {
            const stageSubmitResp = await updateProjectsStagesStatus(data) as ApiResponse;
            if (stageSubmitResp.status === "ok") {
                showSnackbar(stageSubmitResp.message, 'success');
                // if (stageStatus === stageCompletedCode) {
                //     await getProjectReport({ user_id, project_id, dk_stage: stage.dk_stage }, stage.dk_stage);
                // }
                navigate(`/dashboard`);
            } else {
                showSnackbar(stageSubmitResp.message, 'danger');
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, "Failed to update stage status"), 'danger');
            LogError("Failed to update stage status", error, "InfrastructurePlanning");
        } finally {
            setSubmitLoader(false);
        }
    }, [user_id, project_id, stage.dk_stage, stage.project_stage_id, navigate, showSnackbar]);

    const loadStepper = useCallback((): React.ReactNode => {
        const CurrentStepComponent = currentStep.step.components;
        const StepComponent = CurrentStepComponent as React.ComponentType<StepComponentProps>;

        return (
            <Suspense fallback={<div className="p-4 text-center">Loading step...</div>}>
                <StepComponent
                    submitLoader={submitLoader}
                    project_id={project_id}
                    disableFormFields={disableFormFields}
                    setInitValues={setInitValues}
                    isApprover={isApprover}
                    stage={stage}
                    handleProjectRequestSubmit={handleProjectRequestSubmit}
                    setIsAllowNextStep={setIsAllowNextStep}
                    stepTab={stepTab}
                    setStepTab={changeStepTab}
                />
            </Suspense>
        );
    }, [currentStep, submitLoader, project_id, disableFormFields, isApprover, stage, handleProjectRequestSubmit, stepTab, changeStepTab]);

    if (!initValues) return <NotFound />;

    return (
        <div className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid' id='project_stepper'>
            <Formik<ExtendedInfrastructurePlanningTypes>
                innerRef={formikRef}
                validationSchema={validationSchema}
                enableReinitialize={true}
                initialValues={formInitialValues}
                onSubmit={submitStep}
            >
                {({ handleSubmit, setFieldValue, dirty, values }) => (
                    <>
                        <div className={`card position-relative d-flex overflow-visible justify-content-center justify-content-xl-start flex-row-auto ${leftStepperHide ? 'w-100px' : 'w-xl-300px w-xxl-350px w-100'} me-9`}>
                            <StepIconSidebar
                                steps={stepperData}
                                currentStep={currentStep}
                                onStepClick={(key) => goToStep(key, dirty)}
                            />
                        </div>
                        <div className='d-flex flex-row-fluid justify-content-center bg-body rounded'>
                            <Form className='p-8 w-100 position-relative' noValidate id='kt_create_account_form' onSubmit={(e) => handleFormSubmit(e, handleSubmit, setFieldValue)}>
                                <div className='card card-custom'>
                                    <div className='card-header align-items-center'>
                                        <div className='d-flex align-items-center'>
                                            <DynamicSymbol
                                                size={40}
                                                bgClass={`${currentStep.step.iconColor} ${currentStep.step.iconBackground}`}
                                                icon={currentStep.step.icon ? currentStep.step.icon : <CircleDot size={22} />}
                                            />
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <h3 className='card-title align-items-center mb-0 mt-0'>{currentStep.step.title}</h3>
                                                    {currentStep.step.tooltip && <ThemeTooltip text={currentStep.step.tooltip} />}
                                                </div>
                                                {currentStep.step.description && <div className="text-muted fs-7 mt-0">{currentStep.step.description}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='card-body pt-2 px-10 pb-8'>
                                        {loadStepper()}
                                        {(!disableFormFields || (currentStep.isLast && canSubmit)) && (
                                            <div className='d-flex flex-stack pt-4 pb-0 px-4'>
                                                <div className='mr-2'>
                                                    {!currentStep.isFirst && (
                                                        <button onClick={() => prevStep(dirty)} type='button' className='btn btn-md btn-light-primary me-3'>
                                                            <KTIcon iconName='arrow-left' className='fs-4 me-1' />
                                                            Back
                                                        </button>
                                                    )}
                                                </div>
                                                <div>
                                                    {currentStep.step.showContinue ?
                                                        <> {isAllowNextStep &&
                                                            <button disabled={submitLoader} type='button' className='btn btn-md btn-primary ms-3' onClick={handleContinue}>
                                                                <span className='indicator-label d-flex flex-center'>
                                                                    {'Next'}
                                                                    <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
                                                                </span>
                                                            </button>}
                                                        </>
                                                        : <>
                                                            {!currentStep.isLast ? (
                                                                <>
                                                                    <button disabled={submitLoader} type='submit' value='save' className='btn btn-md btn-active-primary btn-color-primary border border-primary btn-active-color-white save-only'>
                                                                        <span className='indicator-label d-flex flex-center'>{submitLoader && values.formAction === "save" ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Saving...</span></> : "Save"}</span>
                                                                    </button>
                                                                    {isAllowNextStep && <button disabled={submitLoader} type='submit' value='save-next' className='btn btn-md btn-primary ms-3'>
                                                                        <span className='indicator-label d-flex flex-center'>
                                                                            {submitLoader && values.formAction === "save-next" ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Saving & opening next...</span></> : "Save & Next"}
                                                                            <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
                                                                        </span>
                                                                    </button>}
                                                                </>
                                                            ) : (
                                                                canSubmit && <button disabled={submitLoader} onClick={handleSubmitStage} type='button' className='btn btn-md btn-primary me-3'>
                                                                    <span className='indicator-label d-flex flex-center'>{submitLoader ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Submitting...</span></> : "Submit"}</span>
                                                                </button>
                                                            )}
                                                        </>
                                                    }
                                                    <Field type="hidden" name="formAction" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Form>
                            <StepChangeAlertModal
                                popupLoader={submitLoader}
                                show={stepChangeAlertModal}
                                onClose={() => setStepChnageAlertModal(false)}
                                onConfirm={confirmStepChange}
                                onSave={() => void handleSaveAndContinue()}
                                message="Your updated data in this step will be lost if you move to another step without saving. Do you want to continue?"
                            />
                            <StepChangeAlertModal
                                popupLoader={submitLoader}
                                show={pendingTabChange !== null}
                                onClose={() => setPendingTabChange(null)}
                                onConfirm={discardAndChangeTab}
                                onSave={() => void saveAndChangeTab()}
                                message={`Your changes in this tab are not saved. Save them before opening another tab, or continue and discard them.`}
                            />
                        </div>
                    </>
                )}
            </Formik>
            <SubmitConfirmationModal
                text="After submitting this stage, you will not be able to make any changes. Are you sure you want to submit this stage?"
                loader={submitLoader}
                show={showSubmitConfirmationModal}
                onClose={() => setShowSubmitConfirmationModal(false)}
                onConfirm={handleConfirmSubmitStage}
            />
        </div>
    )
}

export default InfrastructurePlanning